import type { OverrideRequestRaw, OverrideType } from '../../types';

export interface OverrideTypeMeta {
  label: string;
  audit: 'Standard' | 'Reforçado' | 'Forensic' | 'Dupla aprovação';
  regulatedOnly?: boolean;
  dualApproval?: boolean;
}

/** Labels PT canônicos por tipo (snake_case do schema → PT). */
export const OVERRIDE_TYPE_META: Record<OverrideType, OverrideTypeMeta> = {
  ai_required_validation: { label: 'Validação exigida pela IA', audit: 'Standard' },
  carencia_proativa: { label: 'Carência abordagem proativa', audit: 'Standard' },
  gatilho_protecao: { label: 'Gatilho de proteção', audit: 'Standard' },
  modo_operacao: { label: 'Modo de operação', audit: 'Standard' },
  capability_temporaria: { label: 'Capability temporária', audit: 'Reforçado' },
  legal_capacity_exception: {
    label: 'Exceção de capacidade legal',
    audit: 'Forensic',
    regulatedOnly: true,
  },
  conflict_alert_accept: { label: 'Aceite de ConflictAlert', audit: 'Forensic' },
  h_c_adversarial_strict: {
    label: 'H_c em adversarial-strict',
    audit: 'Dupla aprovação',
    regulatedOnly: true,
    dualApproval: true,
  },
};

export type OverrideDecision = 'direct' | 'type-1' | 'type-5' | 'forensic' | 'type-7';

export interface OverrideDispatch {
  decision: OverrideDecision;
  approveCap: string;
}

/**
 * Dispatch canônico por tipo (D2). NOTA R15: o endpoint genérico /approve (tipos
 * 2/3/4) checa `override-modo-operacao` — não a cap por tipo. O botão espelha isso.
 * `forensic` cobre os tipos 6 e 8 (ambos exigem documentationDocumentIds); o Tipo 8
 * sempre cai em dupla aprovação (ver OVERRIDE_TYPE_META.dualApproval).
 */
export const OVERRIDE_TYPE_DISPATCH: Record<OverrideType, OverrideDispatch> = {
  carencia_proativa: { decision: 'direct', approveCap: 'override-modo-operacao' },
  gatilho_protecao: { decision: 'direct', approveCap: 'override-modo-operacao' },
  modo_operacao: { decision: 'direct', approveCap: 'override-modo-operacao' },
  ai_required_validation: { decision: 'type-1', approveCap: 'override-ai-required-validation' },
  capability_temporaria: { decision: 'type-5', approveCap: 'override-capability-temporaria' },
  legal_capacity_exception: {
    decision: 'forensic',
    approveCap: 'override-legal-capacity-exception',
  },
  conflict_alert_accept: { decision: 'type-7', approveCap: 'override-conflict-alert-accept' },
  h_c_adversarial_strict: { decision: 'forensic', approveCap: 'override-h-c-adversarial-strict' },
};

export interface OverrideCardVM {
  id: string;
  type: OverrideType;
  typeLabel: string;
  audit: OverrideTypeMeta['audit'];
  regulatedOnly: boolean;
  dualApproval: boolean;
  requesterUserId: string;
  conversationId: string | null;
  contextLabel: string;
  /** bondId derivado p/ o DocumentPicker dos tipos forensic (D5) — só quando o contexto é bond. */
  bondId: string | null;
  reason: string;
  status: OverrideRequestRaw['status'];
  createdAt: string;
  expiresAt: string | null;
  payload: unknown;
  decision: OverrideDecision;
  approveCap: string;
}

/** Lê defensivamente o campo `message` do contextPayload (Tipo 1). */
export function readProposedMessage(payload: unknown): string {
  if (payload !== null && typeof payload === 'object' && 'message' in payload) {
    const m = payload.message;
    return typeof m === 'string' ? m : '';
  }
  return '';
}

/** Urgência derivada de expiresAt (sem campo `urgent` no model — D10). <=5min ⇒ urgente. */
export function deriveUrgency(expiresAt: string | null, now: Date = new Date()): boolean {
  if (expiresAt === null) return false;
  const remainingMs = new Date(expiresAt).getTime() - now.getTime();
  return remainingMs <= 5 * 60 * 1000;
}

export function adaptOverrideCard(raw: OverrideRequestRaw): OverrideCardVM {
  const meta = OVERRIDE_TYPE_META[raw.overrideType];
  const dispatch = OVERRIDE_TYPE_DISPATCH[raw.overrideType];
  return {
    id: raw.id,
    type: raw.overrideType,
    typeLabel: meta.label,
    audit: meta.audit,
    regulatedOnly: meta.regulatedOnly ?? false,
    dualApproval: meta.dualApproval ?? false,
    requesterUserId: raw.requesterUserId,
    conversationId: raw.conversationId,
    contextLabel: `${raw.contextResourceType} #${raw.contextResourceId}`,
    bondId: raw.contextResourceType === 'bond' ? raw.contextResourceId : null,
    reason: raw.reason,
    status: raw.status,
    createdAt: raw.createdAt,
    expiresAt: raw.expiresAt,
    payload: raw.contextPayload,
    decision: dispatch.decision,
    approveCap: dispatch.approveCap,
  };
}
